import { describeApiError, type Room } from '@brush/api-client';
import { ErrorNotice, TagPill } from '@brush/ui';
import { Shield } from 'lucide-react';
import { usePatchRoom, usePlatformTags, useTournamentTagUsage } from '../../queries';
import { defaultTagChoices } from '../../settings/defaultTags';

/**
 * The tags a new tournament starts with.
 *
 * Each click is a save — there is no draft here and so nothing for the unsaved-work guard to
 * hold — which is why the whole pane goes read-only while one is in flight rather than letting a
 * second click race the first.
 *
 * <b>It shares the Preferences pane, and says whose it is because of that.</b> This was a pane of
 * its own until the two were grouped; sitting under a heading that means "yours, on this machine",
 * an unlabelled room-wide control is read as a personal one — and this one is stored on the room
 * (`PATCH /api/rooms`), so every operator in the room gets what the last one chose. The line under
 * the heading is what stops that misreading, and it is not decoration.
 */
export function DefaultTagsPane({ room }: { room: Room }) {
  // Every tag the room's tournaments carry, from the room's tag counts (known gap 35).
  const tagUsageQ = useTournamentTagUsage(room.id);
  const platformTagsQ = usePlatformTags();
  const patchRoom = usePatchRoom();

  const platformTags = platformTagsQ.data ?? [];
  const available = defaultTagChoices(platformTags, tagUsageQ.data ?? []);

  const defaultTags = room.defaultTags;
  const isPlatform = (tag: string) =>
    platformTags.some((p) => p.toLowerCase() === tag.toLowerCase());
  const isSelected = (tag: string) =>
    defaultTags.some((t) => t.toLowerCase() === tag.toLowerCase());
  const saving = patchRoom.isPending;

  const toggle = (tag: string) => {
    if (saving) return;
    patchRoom.mutate({
      id: room.id,
      body: {
        defaultTags: isSelected(tag)
          ? defaultTags.filter((t) => t.toLowerCase() !== tag.toLowerCase())
          : [...defaultTags, tag],
      },
    });
  };

  const unselected = available.filter((tag) => !isSelected(tag));

  return (
    <section className="max-w-3xl rounded-xl border border-ink-800/10 bg-white p-6 shadow-sm">
      <h2 className="font-display font-semibold text-ink-950">
        Default tags for new tournaments
      </h2>
      <p className="mt-0.5 text-xs text-ink-800/60">
        Pre-applied whenever you create a tournament. You can still change them per tournament.
      </p>
      <p className="mt-0.5 text-xs text-ink-800/60">
        <strong className="font-medium text-ink-900">The room's, not yours.</strong> Everyone who
        operates {room.name} works from this set, and a change here is a change for all of them.
      </p>

      <div className="mt-5">
        <div className="mb-2 text-[10px] uppercase tracking-wider text-ink-800/50">
          Currently selected ({defaultTags.length})
        </div>
        {defaultTags.length === 0 ? (
          <p className="text-sm italic text-ink-800/40">No default tags selected.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {defaultTags.map((tag) =>
              isPlatform(tag) ? (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-full bg-gold-500/15 px-2.5 py-1 text-xs font-medium text-ink-900"
                >
                  <Shield className="size-2.5" />
                  {tag}
                  <button
                    type="button"
                    onClick={() => toggle(tag)}
                    disabled={saving}
                    aria-label={`Remove ${tag} from defaults`}
                    className="ml-0.5 text-ink-800/50 hover:text-chip-500 disabled:opacity-50"
                  >
                    &times;
                  </button>
                </span>
              ) : (
                <TagPill key={tag} label={tag} kind="room" onRemove={() => toggle(tag)} />
              ),
            )}
          </div>
        )}
        <p className="mt-2 text-[10px] text-ink-800/40">
          Removing a platform tag here only affects new tournaments — it stays on tournaments that
          already carry it.
        </p>
      </div>

      <div className="mt-4 border-t border-ink-800/10 pt-4">
        <div className="mb-2 text-[10px] uppercase tracking-wider text-ink-800/50">
          Available tags
        </div>
        {available.length === 0 ? (
          <p className="text-sm text-ink-800/50">
            No tags yet. Tags used on your tournaments, or defined by the platform, appear here.
          </p>
        ) : unselected.length === 0 ? (
          <p className="text-sm italic text-ink-800/40">All available tags are already selected.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {unselected.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggle(tag)}
                disabled={saving}
                className={
                  isPlatform(tag)
                    ? 'inline-flex items-center gap-1 rounded-md border border-gold-500/40 bg-gold-500/10 px-2 py-1 text-xs text-ink-900 hover:bg-gold-500/20 disabled:opacity-50'
                    : 'inline-flex items-center gap-1 rounded-md border border-ink-800/15 bg-felt-50/60 px-2 py-1 text-xs text-ink-800/70 hover:border-felt-500 disabled:opacity-50'
                }
              >
                {isPlatform(tag) ? <Shield className="size-3" /> : null}+ {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      <ErrorNotice message={describeApiError(patchRoom.error)} className="mt-4" />
    </section>
  );
}
